import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from './Button';
import TextField from './TextField';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';

/**
 * Редактор стандартного контента с поддержкой Markdown и предпросмотром (одновременно)
 * @param {string} content - Содержимое блока (markdown)
 * @param {function} onChange - Функция обработки изменений
 */
const StandardContentEditor = ({ content, onChange }) => {
  const { t } = useTranslation(['lessons', 'common']);
  // Локальное состояние для буферизации изменений
  const [localContent, setLocalContent] = useState(content || '');
  // Справка по Markdown
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);
  // Отслеживаем, изменился ли контент с момента последнего сохранения
  const [isDirty, setIsDirty] = useState(false);
  // Таймер для отложенного сохранения
  const saveTimeoutRef = useRef(null);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Синхронизируем локальное состояние с пропсами при их изменении
  useEffect(() => {
    setLocalContent(content || '');
    setIsDirty(false);
  }, [content]);

    // Сохраняем изменения в родительский компонент
  const saveChangesToParent = useCallback(() => {
    if (isDirty) {
      onChange(localContent);
      setIsDirty(false);
    }
  }, [localContent, isDirty, onChange]);

  // Эффект для отложенного сохранения с debounce
  useEffect(() => {
    if (isDirty) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveChangesToParent();
      }, 1000);
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [localContent, isDirty, saveChangesToParent]);

  // Обработчик изменения контента
  const handleContentChange = (e) => {
    setLocalContent(e.target.value);
    setIsDirty(true);
  };

  // Обработчик потери фокуса
  const handleBlur = () => {
    saveChangesToParent();
  };

  // Справка по Markdown
  const MarkdownHelp = () => (
    <Paper elevation={0} sx={{ mb: 2, maxWidth: '100%', overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        {t('lessons:editor.markdownHelpTitle')}
      </Typography>
      <Box
        component="pre"
        sx={{
          backgroundColor: 'background.default',
          p: 1,
          borderRadius: 1,
          fontSize: '0.85rem',
          overflowX: 'auto',
        }}
      >
        {`# ${t('lessons:editor.exampleHeading1')}\n## ${t(
          'lessons:editor.exampleHeading2'
        )}\n\n**${t('lessons:editor.exampleBold')}**\n*${t(
          'lessons:editor.exampleItalic'
        )}*\n\n- ${t('lessons:editor.exampleListItem1')}\n- ${t(
          'lessons:editor.exampleListItem2'
        )}\n\n[${t('lessons:editor.exampleLink')}](https://example.com)\n\n> ${t(
          'lessons:editor.exampleQuote'
        )}\n\`\`\`\n${t('lessons:editor.exampleCodeBlock')}\n\`\`\`\n`}
      </Box>
      <Button
        size="small"
        variant="outlined"
        sx={{ mt: 2 }}
        onClick={() => setShowMarkdownHelp(false)}
      >
        {t('lessons:editor.closeHelp')}
      </Button>
    </Paper>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {t('lessons:editor.standardContent')}
        </Typography>
        <Tooltip title={t('lessons:editor.markdownGuide')}>
          <IconButton onClick={() => setShowMarkdownHelp((v) => !v)}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {showMarkdownHelp && <MarkdownHelp />}
      <Grid container spacing={2} alignItems="stretch">
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 180,
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={isSmall ? 8 : 14}
            label={t('lessons:editor.content')}
            value={localContent}
            onChange={handleContentChange}
            onBlur={handleBlur}
            helperText={t('lessons:editor.markdownHelp')}
            placeholder={t('lessons:editor.markdownPlaceholder')}
            sx={{ minHeight: 180, flex: 1, display: 'flex' }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default StandardContentEditor;
